import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SignupStateService } from '../../services/signup-state.service';
import { COUNTRY_PHONE_OPTIONS, PASOS_EMPRESA } from '../../constants/signup.constants';
import { mapNacionalidad } from '../../utils/residencia-nacionalidad';
import { erutEsDeOtraPersona } from '../../utils/erut-representante';

/**
 * Paso 9 (empresa): datos del representante legal.
 *
 * Antes de mostrar el formulario cruza el documento verificado contra el e-RUT.
 * La declaracion PEP vive en el paso siguiente, y es ese el que persiste todo.
 */
@Component({
  selector: 'app-representante-legal-step',
  templateUrl: './representante-legal-step.component.html',
  styleUrls: ['./representante-legal-step.component.sass'],
})
export class RepresentanteLegalStepComponent implements OnInit {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  form!: FormGroup;
  countries = COUNTRY_PHONE_OPTIONS;

  // Cruce con el e-RUT
  erutMismatch = false;

  constructor(
    private fb: FormBuilder,
    public signupState: SignupStateService
  ) {}

  ngOnInit(): void {
    const state = this.signupState.currentState;

    // Prerrequisitos: OCR del documento y e-RUT analizado
    if (!state.ocrData) {
      this.signupState.goToStep(PASOS_EMPRESA.OCR);
      return;
    }
    if (!state.erutAnalysis) {
      this.signupState.goToStep(PASOS_EMPRESA.ERUT);
      return;
    }

    this.initForm();
    this.checkErutMatch();
  }

  // ==================== FORMULARIO ====================

  private initForm(): void {
    const ocr = this.signupState.currentState.ocrData;
    const saved = this.signupState.representanteLegal;

    this.form = this.fb.group({
      // Del OCR: se muestran pero no se editan
      nombres: [{ value: ocr?.firstName || '', disabled: true }, Validators.required],
      apellidos: [{ value: ocr?.lastName || '', disabled: true }, Validators.required],
      rut: [
        { value: ocr?.documentNumber || ocr?.RUN || '', disabled: true },
        Validators.required,
      ],

      // Completados por el usuario
      // La nacionalidad del OCR viene en la forma del documento ("CHILENA") y
      // el select compara contra nombres de pais ("Chile"): sin normalizar no
      // matchea nunca y el campo queda vacio siendo obligatorio. Es el mismo
      // util que usa el registro de personas.
      nacionalidad: [
        saved?.nacionalidad || mapNacionalidad(ocr?.nationality) || '',
        Validators.required,
      ],
      direccion_particular: [saved?.direccion_particular || '', Validators.required],
      tipo_departamento: [saved?.tipo_departamento || ''],
      comuna: [saved?.comuna || '', Validators.required],
      ciudad: [saved?.ciudad || '', Validators.required],
      pais_direccion: [saved?.pais_direccion || '', Validators.required],
      porcentaje_participacion: [
        saved?.porcentaje_participacion || '',
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      fecha_vencimiento_dni: [
        saved?.fecha_vencimiento_dni || ocr?.expirationDate || '',
        Validators.required,
      ],
    });
  }

  // ==================== CRUCE CON EL e-RUT ====================

  /**
   * Vuelve a evaluar el cruce con el e-RUT para dibujar la comparacion y frenar
   * el envio. La regla vive en `utils/erut-representante`: el paso del OCR la
   * consulta antes, para no hacer capturar de mas.
   */
  private checkErutMatch(): void {
    const ocr = this.signupState.currentState.ocrData;

    this.erutMismatch = erutEsDeOtraPersona(
      ocr?.documentNumberClean || ocr?.documentNumber,
      this.signupState.erutAnalysis?.rutUsuario
    );
  }

  get rutRepresentanteLabel(): string {
    const ocr = this.signupState.currentState.ocrData;
    return ocr?.documentNumber || ocr?.RUN || '—';
  }

  get rutErutLabel(): string {
    return this.signupState.erutAnalysis?.rutUsuario || '—';
  }

  get usuarioErutLabel(): string {
    return this.signupState.erutAnalysis?.usuario || '—';
  }

  /** Vuelve al paso del e-RUT para cargar otro documento. */
  async loadAnotherErut(): Promise<void> {
    this.signupState.setErutAnalysis(null);
    await this.signupState.goToStep(PASOS_EMPRESA.ERUT);
  }

  /**
   * Salida del panel de desencuentro cuando el que esta mal es el documento y
   * no el e-RUT.
   *
   * No usa `prevStep()` a proposito: el paso anterior es la selfie, que no tiene
   * nada que ver con el problema — y ademas a esta pantalla se puede llegar
   * directo desde el OCR, sin selfie tomada, con lo cual "volver" mandaria a
   * sacarse una foto que despues no sirve para nada.
   */
  async volverAlDocumento(): Promise<void> {
    await this.signupState.goToStep(PASOS_EMPRESA.OCR);
  }

  // ==================== ENVIO ====================

  async onSubmit(): Promise<void> {
    if (this.erutMismatch) return;

    if (this.form.invalid) {
      this.markFormAsTouched();
      this.onError.emit('Completa todos los campos requeridos.');
      return;
    }

    const values = this.form.getRawValue();
    const saved = this.signupState.representanteLegal;

    // Los campos PEP los completa el paso siguiente: se conservan si ya paso por ahi
    this.signupState.setRepresentanteLegal({
      ...values,
      es_pep: saved?.es_pep ?? null,
      pep_funcion: saved?.pep_funcion || '',
      pep_institucion: saved?.pep_institucion || '',
      pep_fecha_inicio: saved?.pep_fecha_inicio || '',
      pep_fecha_termino: saved?.pep_fecha_termino || '',
      pep_deteccion_automatica: saved?.pep_deteccion_automatica || false,
    });

    await this.signupState.nextStep();
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && control.touched;
  }

  getFlagForCountry(countryName: string): string {
    const country = this.countries.find(
      (c) => c.name.toLowerCase() === countryName?.toLowerCase()
    );
    return country ? country.flag : 'chile.png';
  }

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }
}
